// AO1a (2026-10-12) — 자동발주 탭(auto.tsx)·재료 탭(low.tsx) 공통 순수 함수.
//   화면과 scripts/verify-auto-order-d1-d10.ts 가 같은 함수를 쓴다. React·SDK import 없음.
//   D1 부족수량 정본 (codex algorithms/ingredient-centered-inventory-sync NAMING.md 부족수량)
//   D10 발주중 수량 P = 작성 중(draft)·발송(sent) 발주 전체 페이지 (GET /v1/orders · limit 최대 100 · cursor · has_more).

export function roundOrderQuantity(value: number): number {
  return Math.round(value * 10000) / 10000;
}

// ─── D1 부족수량 ──────────────────────────────────────────────────────

/**
 * 부족수량 — minStock > 0 && C > minStock 이면 0, 아니면 max(0, (targetStock > 0 ? targetStock : minStock) − C).
 * C 없음(null) = 0 (기존 그대로). API material-shortage.ts 와 같은 식.
 */
export function calculateMaterialShortage(
  comparableQuantity: number | null,
  minStock: number,
  targetStock: number,
): number {
  if (comparableQuantity == null) return 0;
  if (minStock > 0 && comparableQuantity > minStock) return 0;
  const refillTarget = targetStock > 0 ? targetStock : minStock;
  return Math.max(0, refillTarget - comparableQuantity);
}

export interface ShortageMaterial {
  comparableQuantity: number | null;
  stock: number;
  minStock: number;
  targetStock: number;
}

/**
 * 자동발주 탭 부족수량 — 기존 비교수량 전처리 그대로: 만료 재료 = 0 (가용 재고 제외), 아니면 comparableQuantity ?? stock.
 * 만료 재료는 API·재료 탭(C 그대로)과 결과가 다를 수 있다.
 */
export function autoOrderMaterialShortage(material: ShortageMaterial, expired: boolean): number {
  const comparableQuantity = expired ? 0 : material.comparableQuantity ?? material.stock;
  return calculateMaterialShortage(comparableQuantity, material.minStock, material.targetStock);
}

export type StockShortageIssueGroup = 'no-stock' | 'below-safety' | 'below-target';

/**
 * 재료 탭 이슈 패널의 재고 부족 분류 — 재고 없음·안전수량 이하 조건은 기존 그대로,
 * 그 밖에 부족 > 0 (예: 안전0·목표5·C2) = 기존 below-target(적정 미만) 분류.
 */
export function stockShortageIssueGroup(
  row: { stock: number; minStock: number; comparableQuantity: number | null },
  shortage: number,
): StockShortageIssueGroup | null {
  if (row.stock <= 0 && (row.minStock > 0 || shortage > 0)) return 'no-stock';
  if (row.comparableQuantity != null && shortage > 0 && row.comparableQuantity <= Math.max(row.minStock, 0)) {
    return 'below-safety';
  }
  if (shortage > 0) return 'below-target';
  return null;
}

// ─── D10 발주중 수량 P — 작성 중·발송 발주 전체 페이지 ─────────────────

export const PENDING_ORDER_STATUSES = ['draft', 'sent'] as const;
export type PendingOrderStatus = (typeof PENDING_ORDER_STATUSES)[number];
/** API 목록 최대 (apps/api/src/common/list-helper.ts clampLimit). */
export const PENDING_ORDER_PAGE_LIMIT = 100;
/** status별 페이지 안전 한도 — 넘으면 불완전 조회 (부분 합계 반환 X). */
export const PENDING_ORDER_MAX_PAGES = 1000;

export interface PendingOrderLine {
  inventoryId: string;
  quantity: string | number;
}

export interface PendingOrderLike {
  id: string;
  status: string;
  lines: PendingOrderLine[];
}

export interface PendingOrderPage<T> {
  items: T[];
  cursor: string | null;
  has_more: boolean;
}

export interface PendingOrderPageRequest {
  status: PendingOrderStatus;
  limit: number;
  cursor: string | null;
}

export type FetchPendingOrderPage<T> = (request: PendingOrderPageRequest) => Promise<PendingOrderPage<T>>;

export type IncompletePendingOrdersReason =
  | 'missing-cursor'
  | 'repeated-cursor'
  | 'duplicate-conflict'
  | 'invalid-page'
  | 'page-limit';

export class IncompletePendingOrdersError extends Error {
  readonly reason: IncompletePendingOrdersReason;

  constructor(reason: IncompletePendingOrdersReason, message: string) {
    super(message);
    this.name = 'IncompletePendingOrdersError';
    this.reason = reason;
  }
}

/** 발주중 수량 기여 — 기존 합산 의미 그대로: draft·sent 발주의 inventoryId 있고 수량이 유한한 양수인 품목. */
function pendingOrderContributions(order: PendingOrderLike): Array<{ inventoryId: string; quantity: number }> {
  if (order.status !== 'draft' && order.status !== 'sent') return [];
  const contributions: Array<{ inventoryId: string; quantity: number }> = [];
  for (const line of order.lines) {
    const quantity = Number(line.quantity);
    if (!line.inventoryId || !Number.isFinite(quantity) || quantity <= 0) continue;
    contributions.push({ inventoryId: line.inventoryId, quantity });
  }
  return contributions;
}

function pendingContributionKey(order: PendingOrderLike): string {
  return pendingOrderContributions(order)
    .map(({ inventoryId, quantity }) => `${inventoryId}\u0000${quantity}`)
    .sort()
    .join('\u0001');
}

function isPendingOrderPage(page: unknown): page is PendingOrderPage<PendingOrderLike> {
  if (!page || typeof page !== 'object') return false;
  const value = page as { items?: unknown; has_more?: unknown };
  return (
    Array.isArray(value.items) &&
    typeof value.has_more === 'boolean' &&
    value.items.every(
      (order) =>
        !!order &&
        typeof order === 'object' &&
        typeof (order as { id?: unknown }).id === 'string' &&
        Array.isArray((order as { lines?: unknown }).lines),
    )
  );
}

/**
 * 작성 중(draft) 발주를 limit 100·cursor로 has_more=false까지 모두 읽은 뒤 발송(sent) 발주를 같은 방식으로 읽는다.
 * 같은 주문 ID는 한 번만 담고, 같은 ID 사본의 재료·수량 기여가 다르면 불완전 조회로 실패한다 (섞어 합산 X).
 * 페이지 실패(fetchPage throw)·has_more인데 cursor 없음·반복 cursor·형식 오류·페이지 한도 초과 = 예외
 * — 부분 목록·0을 완료로 반환하지 않는다. count는 페이지 건수라 전체 건수로 쓰지 않는다.
 * 원자 snapshot이 아니다: 조회 중 동시 상태 변경·cursor 행 삭제까지 누락 0을 보장하지 않는다.
 */
export async function loadAllPendingOrders<T extends PendingOrderLike>(
  fetchPage: FetchPendingOrderPage<T>,
): Promise<T[]> {
  const byId = new Map<string, T>();
  const orders: T[] = [];
  for (const status of PENDING_ORDER_STATUSES) {
    let cursor: string | null = null;
    const seenCursors = new Set<string>();
    for (let page = 0; ; page += 1) {
      if (page >= PENDING_ORDER_MAX_PAGES) {
        throw new IncompletePendingOrdersError(
          'page-limit',
          `발주중 수량 조회가 ${status} ${PENDING_ORDER_MAX_PAGES}페이지를 넘었습니다.`,
        );
      }
      const envelope: unknown = await fetchPage({ status, limit: PENDING_ORDER_PAGE_LIMIT, cursor });
      if (!isPendingOrderPage(envelope)) {
        throw new IncompletePendingOrdersError('invalid-page', `발주중 수량 조회 응답 형식 오류 (${status})`);
      }
      const typed = envelope as PendingOrderPage<T>;
      for (const order of typed.items) {
        const existing = byId.get(order.id);
        if (!existing) {
          byId.set(order.id, order);
          orders.push(order);
        } else if (pendingContributionKey(existing) !== pendingContributionKey(order)) {
          throw new IncompletePendingOrdersError(
            'duplicate-conflict',
            `같은 발주서(${order.id})의 품목·수량이 조회 중 달라졌습니다.`,
          );
        }
      }
      if (!typed.has_more) break;
      const next = typed.cursor;
      if (!next) {
        throw new IncompletePendingOrdersError('missing-cursor', `발주중 수량 조회 다음 페이지 cursor 없음 (${status})`);
      }
      if (seenCursors.has(next)) {
        throw new IncompletePendingOrdersError('repeated-cursor', `발주중 수량 조회 cursor 반복 (${status})`);
      }
      seenCursors.add(next);
      cursor = next;
    }
  }
  return orders;
}

/** 발주중 수량 P — 재료별 draft·sent 기여 합 (기존 필터·반올림 그대로). */
export function buildPendingQuantityByInventoryId(orders: PendingOrderLike[]): Map<string, number> {
  const quantities = new Map<string, number>();
  for (const order of orders) {
    for (const { inventoryId, quantity } of pendingOrderContributions(order)) {
      quantities.set(inventoryId, roundOrderQuantity((quantities.get(inventoryId) ?? 0) + quantity));
    }
  }
  return quantities;
}

// ─── 불완전 조회 차단 ─────────────────────────────────────────────────

export type PendingOrdersGateReason = 'loading' | 'error' | 'refetching';

export interface PendingOrdersQueryState {
  status: 'pending' | 'error' | 'success';
  fetchStatus: 'fetching' | 'paused' | 'idle';
}

/**
 * 후보 확정·후보 기반 발주서 생성 허용 = 마지막 전체 조회 성공 + 진행 중 조회 없음.
 * 첫 로딩 · 오류(이전 데이터가 남은 stale cache 포함) · 백그라운드 재조회 중 = 차단.
 */
export function pendingOrdersGate(
  state: PendingOrdersQueryState | null | undefined,
): { ready: boolean; reason: PendingOrdersGateReason | null } {
  if (!state || state.status === 'pending') return { ready: false, reason: 'loading' };
  if (state.status === 'error') return { ready: false, reason: 'error' };
  if (state.fetchStatus !== 'idle') return { ready: false, reason: 'refetching' };
  return { ready: true, reason: null };
}
